const BODY_HTML_PLACEHOLDER = '<!-- bodyHtml -->'

const RUNTIME_MARKDOWN_LINK_REPLACEMENTS = [
  {
    pattern: /\[Unsubscribe\]\(\{\{unsubscribeUrl\}\}\)/gi,
    replacement: '<a href="{{unsubscribeUrl}}">Unsubscribe</a>'
  },
  {
    pattern: /\[\{\{unsubscribeUrl\}\}\]Unsubscribe/gi,
    replacement: '<a href="{{unsubscribeUrl}}">Unsubscribe</a>'
  },
  {
    pattern: /\[([^\]]+)\]\(\{\{ctaUrl\}\}\)/gi,
    replacement: '<a href="{{ctaUrl}}">$1</a>'
  }
]

const ENCODED_RUNTIME_PLACEHOLDER_HREFS = [
  {
    pattern: /href=(["'])%7B%7BunsubscribeUrl%7D%7D\1/gi,
    replacement: 'href=$1{{unsubscribeUrl}}$1'
  },
  {
    pattern: /href=(["'])%7B%7BctaUrl%7D%7D\1/gi,
    replacement: 'href=$1{{ctaUrl}}$1'
  }
]

const BROKEN_UNSUBSCRIBE_LITERALS = [
  {
    pattern: /\[%7B%7BunsubscribeUrl%7D%7D\]Unsubscribe/gi,
    replacement: '<a href="{{unsubscribeUrl}}">Unsubscribe</a>'
  },
  {
    pattern: /\[\{\{unsubscribeUrl\}\}\]Unsubscribe/gi,
    replacement: '<a href="{{unsubscribeUrl}}">Unsubscribe</a>'
  }
]

const MJML_BODY_SECTION_REGEX = /<table[^>]*class="[^"]*\bmjml-body\b[^"]*"[^>]*>[\s\S]*?<\/table>/i

const MARKDOWN_INNER_DIV_REGEX =
  /(<td[^>]*class="[^"]*markdown[^"]*"[^>]*>[\s\S]*?<div[^>]*style="[^"]*font-size:\s*15px[^"]*"[^>]*>)(.*?)(<\/div>)/i

const MARKDOWN_SECTION_REGEX =
  /(<(div|td)[^>]*class="[^"]*markdown[^"]*"[^>]*>)(.*?)(<\/\2>)/is

const EMAIL_TITLE_CELL_REGEX = /(<td[^>]*font-size:\s*20px[^>]*>)([\s\S]*?)(<\/td>)/is

const MARKDOWN_SECTION_START_REGEX = /class="[^"]*\bmarkdown\b[^"]*"|<!--\s*bodyHtml\s*-->/i

const DISPLAY_TITLE_CELL_REGEX =
  /(<td[^>]*class="[^"]*display-title[^"]*"[^>]*>[\s\S]*?<div[^>]*>)([\s\S]*?)(<\/div>)/i

const RESORT_INVITE_TITLE_CELL_REGEX =
  /(<td[^>]*class="[^"]*resort-invite-title[^"]*"[^>]*>[\s\S]*?<div[^>]*>)([\s\S]*?)(<\/div>)/i

const EMAIL_TITLE_DIV_STYLE_REGEX =
  /(<div[^>]*style="[^"]*font-size:\s*20px[^"]*"[^>]*>)([\s\S]*?)(<\/div>)/i

const CTA_CONTAINER_REGEX =
  /<(table|td|div)\b(?=[^>]*class=["'][^"']*\b(?:mjml-cta|resort-solid-cta|editorial-pill-cta-bulletproof)\b[^"']*["'])[^>]*>[\s\S]*?<\/\1>/gi

function upsertFullWidthCtaLinkStyle(tag) {
  const requiredStyles =
    'display:block !important;width:100% !important;height:100% !important;box-sizing:border-box !important;'

  if (/style=(["'])/i.test(tag)) {
    return tag.replace(/style=(["'])(.*?)\1/i, (_match, quote, styles) => {
      const cleaned = styles
        .replace(/\bdisplay\s*:[^;"]*;?/gi, '')
        .replace(/\bwidth\s*:[^;"]*;?/gi, '')
        .replace(/\bheight\s*:[^;"]*;?/gi, '')
        .replace(/\bbox-sizing\s*:[^;"]*;?/gi, '')

      return `style=${quote}${cleaned}${requiredStyles}${quote}`
    })
  }

  return tag.replace(/>$/, ` style="${requiredStyles}">`)
}

/**
 * MJML paints the CTA background on its table cell. Some email clients then leave the anchor
 * sized to its label, so only the text is clickable even though the whole cell looks like a
 * button. Make the anchor fill each known CTA container without changing ordinary text links.
 */
export function makeCtaLinksFullyClickable(sourceHtml) {
  if (!sourceHtml) {
    return sourceHtml
  }

  return sourceHtml.replace(CTA_CONTAINER_REGEX, containerHtml =>
    containerHtml.replace(/<a\b[^>]*>/i, upsertFullWidthCtaLinkStyle)
  )
}

export function fixMarkdownCellVisibility(sourceHtml) {
  if (!sourceHtml) {
    return sourceHtml
  }

  let html = sourceHtml.replace(/<td\b(?=[^>]*class="[^"]*\bmarkdown\b)[^>]*>/gi, tag =>
    tag.replace(/font-size:\s*0px/gi, 'font-size:15px')
  )

  html = html.replace(
    /(<td[^>]*class="[^"]*\bmarkdown\b[^"]*"[^>]*>[\s\S]*?<div[^>]*style=")([^"]*)("[^>]*>)/gi,
    (_match, open, style, close) =>
      `${open}${style.replace(/font-size:\s*0px/gi, 'font-size:15px')}${close}`
  )

  return html
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function wrapBodyContent(bodyHtmlContent) {
  return bodyHtmlContent || ''
}

function wrapBodyInMarkdownDiv(bodyHtmlContent) {
  return `<div style="font-family:Inter, Arial, sans-serif;font-size:15px;line-height:1.6;text-align:left;color:#3b4c56;">${bodyHtmlContent}</div>`
}

function clearMarkdownSection(sectionHtml) {
  if (!MARKDOWN_SECTION_REGEX.test(sectionHtml)) {
    return sectionHtml
  }

  return sectionHtml.replace(MARKDOWN_SECTION_REGEX, (match, openTag, _tagName, _content, closeTag) => {
    return `${openTag}${wrapBodyInMarkdownDiv('')}${closeTag}`
  })
}

function injectIntoMarkdownSection(sectionHtml, bodyHtmlContent) {
  const isEmptyBody = !bodyHtmlContent || !String(bodyHtmlContent).trim()

  if (sectionHtml.includes(BODY_HTML_PLACEHOLDER)) {
    if (isEmptyBody) {
      return sectionHtml
    }

    return sectionHtml.replace(BODY_HTML_PLACEHOLDER, wrapBodyContent(bodyHtmlContent))
  }

  if (isEmptyBody) {
    return clearMarkdownSection(sectionHtml)
  }

  const wrappedBodyContent = wrapBodyContent(bodyHtmlContent)
  const innerDivMatch = sectionHtml.match(MARKDOWN_INNER_DIV_REGEX)

  if (innerDivMatch) {
    return (
      sectionHtml.slice(0, innerDivMatch.index + innerDivMatch[1].length) +
      wrappedBodyContent +
      sectionHtml.slice(innerDivMatch.index + innerDivMatch[0].length - innerDivMatch[3].length)
    )
  }

  if (!MARKDOWN_SECTION_REGEX.test(sectionHtml)) {
    return sectionHtml
  }

  const bodyBlock = wrapBodyInMarkdownDiv(wrappedBodyContent)

  return sectionHtml.replace(
    MARKDOWN_SECTION_REGEX,
    (match, openTag, _tagName, _content, closeTag) => `${openTag}${bodyBlock}${closeTag}`
  )
}

function injectIntoMjmlBodySection(sourceHtml, bodyHtmlContent) {
  const bodySectionMatch = sourceHtml.match(MJML_BODY_SECTION_REGEX)

  if (!bodySectionMatch) {
    return null
  }

  const updatedSection = injectIntoMarkdownSection(bodySectionMatch[0], bodyHtmlContent)

  return sourceHtml.replace(bodySectionMatch[0], updatedSection)
}

export function injectInviteEmailBodyHtml(sourceHtml, bodyHtmlContent) {
  if (!sourceHtml) {
    return sourceHtml
  }

  const scopedInjection = injectIntoMjmlBodySection(sourceHtml, bodyHtmlContent)

  if (scopedInjection) {
    return fixMarkdownCellVisibility(scopedInjection)
  }

  if (sourceHtml.includes(BODY_HTML_PLACEHOLDER)) {
    if (!bodyHtmlContent || !String(bodyHtmlContent).trim()) {
      return fixMarkdownCellVisibility(sourceHtml)
    }

    return fixMarkdownCellVisibility(
      sourceHtml.replace(BODY_HTML_PLACEHOLDER, wrapBodyContent(bodyHtmlContent))
    )
  }

  return fixMarkdownCellVisibility(injectIntoMarkdownSection(sourceHtml, bodyHtmlContent))
}

function findMarkdownSectionIndex(sourceHtml, bodyIndex) {
  const slice = sourceHtml.slice(bodyIndex)
  const match = slice.match(MARKDOWN_SECTION_START_REGEX)

  if (!match || match.index == null) {
    return -1
  }

  return bodyIndex + match.index
}

function replaceEmailTitleInBodySection(sourceHtml, encodedTitle) {
  const bodyIndex = sourceHtml.search(/mjml-body/i)

  if (bodyIndex < 0) {
    return null
  }

  const markdownIndex = findMarkdownSectionIndex(sourceHtml, bodyIndex)

  if (markdownIndex < 0) {
    return null
  }

  const section = sourceHtml.slice(bodyIndex, markdownIndex)

  if (DISPLAY_TITLE_CELL_REGEX.test(section)) {
    const updatedSection = section.replace(
      DISPLAY_TITLE_CELL_REGEX,
      (_match, openTag, _content, closeTag) => `${openTag}${encodedTitle}${closeTag}`
    )

    return sourceHtml.slice(0, bodyIndex) + updatedSection + sourceHtml.slice(markdownIndex)
  }

  if (RESORT_INVITE_TITLE_CELL_REGEX.test(section)) {
    const updatedSection = section.replace(
      RESORT_INVITE_TITLE_CELL_REGEX,
      (_match, openTag, _content, closeTag) => `${openTag}${encodedTitle}${closeTag}`
    )

    return sourceHtml.slice(0, bodyIndex) + updatedSection + sourceHtml.slice(markdownIndex)
  }

  const divMatch = section.match(EMAIL_TITLE_DIV_STYLE_REGEX)

  if (divMatch) {
    const absoluteIndex = bodyIndex + divMatch.index

    return (
      sourceHtml.slice(0, absoluteIndex + divMatch[1].length) +
      encodedTitle +
      sourceHtml.slice(absoluteIndex + divMatch[0].length - divMatch[3].length)
    )
  }

  const titleMatch = section.match(EMAIL_TITLE_CELL_REGEX)

  if (titleMatch) {
    const absoluteIndex = bodyIndex + titleMatch.index

    return (
      sourceHtml.slice(0, absoluteIndex + titleMatch[1].length) +
      encodedTitle +
      sourceHtml.slice(absoluteIndex + titleMatch[0].length - titleMatch[3].length)
    )
  }

  return null
}

export function injectInviteEmailTitle(sourceHtml, emailTitle) {
  if (!sourceHtml || emailTitle == null || emailTitle === '') {
    return sourceHtml
  }

  const encodedTitle = escapeHtml(emailTitle)

  if (sourceHtml.includes('{{emailTitle}}')) {
    return sourceHtml.replace(/\{\{emailTitle\}\}/g, encodedTitle)
  }

  const replacedInBodySection = replaceEmailTitleInBodySection(sourceHtml, encodedTitle)

  if (replacedInBodySection) {
    return replacedInBodySection
  }

  if (!EMAIL_TITLE_CELL_REGEX.test(sourceHtml)) {
    return sourceHtml
  }

  return sourceHtml.replace(
    EMAIL_TITLE_CELL_REGEX,
    (_match, openTag, _content, closeTag) => `${openTag}${encodedTitle}${closeTag}`
  )
}

function removeHtmlBlockByClass(sourceHtml, className) {
  const classPattern = new RegExp(`class="[^"]*\\b${className}\\b[^"]*"`, 'i')
  const tableMatch = sourceHtml.match(
    new RegExp(`<table[^>]*${classPattern.source}[^>]*>`, 'i')
  )
  const divMatch = sourceHtml.match(new RegExp(`<div[^>]*${classPattern.source}[^>]*>`, 'i'))
  const match = divMatch || tableMatch

  if (!match || match.index == null) {
    return sourceHtml
  }

  const tagName = match[0].startsWith('<div') ? 'div' : 'table'
  const start = match.index
  let depth = 0
  let index = start

  while (index < sourceHtml.length) {
    if (sourceHtml.startsWith(`<${tagName}`, index)) {
      depth += 1
    }

    if (sourceHtml.startsWith(`</${tagName}>`, index)) {
      depth -= 1

      if (depth === 0) {
        return sourceHtml.slice(0, start) + sourceHtml.slice(index + `</${tagName}>`.length)
      }
    }

    index += 1
  }

  return sourceHtml
}

export function suppressEmptyOptionalRichtextSections(sourceHtml, richtextHtmlMap = {}) {
  if (!sourceHtml) {
    return sourceHtml
  }

  if (!`${richtextHtmlMap.personalHtml || ''}`.trim()) {
    return removeHtmlBlockByClass(sourceHtml, 'mjml-personal')
  }

  return sourceHtml
}

function hasDiscoverButtonLabel(variables, buttonNumber) {
  return `${variables?.[`discoverBtn${buttonNumber}`] || ''}`.trim().length > 0
}

function removeDiscoverButtonWrap(sourceHtml, buttonNumber) {
  const pattern = new RegExp(
    `<td[^>]*data-discover-button="${buttonNumber}"[^>]*>[\\s\\S]*?<\\/table>\\s*<\\/td>`,
    'gi'
  )

  return sourceHtml.replace(pattern, '')
}

function removeEmptyDiscoverButtonRows(sourceHtml) {
  return sourceHtml.replace(/<tr(?:[^>]*)>\s*<\/tr>/gi, '')
}

function removeEmptyDiscoverButtonSections(sourceHtml) {
  return removeHtmlBlockByClass(sourceHtml, 'resort-discover-buttons-section')
}

export function suppressEmptyResortDiscoverButtons(sourceHtml, variables = {}) {
  if (!sourceHtml) {
    return sourceHtml
  }

  let html = sourceHtml

  for (let buttonNumber = 1; buttonNumber <= 4; buttonNumber += 1) {
    if (!hasDiscoverButtonLabel(variables, buttonNumber)) {
      html = removeDiscoverButtonWrap(html, buttonNumber)
    }
  }

  html = removeEmptyDiscoverButtonRows(html)

  const hasAnyDiscoverButton = [1, 2, 3, 4].some(buttonNumber =>
    hasDiscoverButtonLabel(variables, buttonNumber)
  )

  if (!hasAnyDiscoverButton) {
    html = removeEmptyDiscoverButtonSections(html)
  }

  return html
}

function hasEditorialStoryButtonLabel(variables, columnKey) {
  return `${variables?.[`${columnKey}LinkText`] || ''}`.trim().length > 0
}

function removeEditorialStoryButtonWrap(sourceHtml, columnKey) {
  let html = sourceHtml

  const bulletproofPattern = new RegExp(
    `<table[^>]*class="[^"]*editorial-pill-cta-bulletproof[^"]*"[^>]*>[\\s\\S]*?data-editorial-story-button="${columnKey}"[\\s\\S]*?<\\/table>`,
    'gi'
  )
  html = html.replace(bulletproofPattern, '')

  const legacyTdPattern = new RegExp(
    `<td[^>]*data-editorial-story-button="${columnKey}"[^>]*>[\\s\\S]*?<\\/table>\\s*<\\/td>`,
    'gi'
  )
  html = html.replace(legacyTdPattern, '')

  const anchorPattern = new RegExp(
    `<a[^>]*data-editorial-story-button="${columnKey}"[^>]*>[\\s\\S]*?<\\/a>`,
    'gi'
  )

  return html.replace(anchorPattern, '')
}

export function resolveEditorialPillTextColor(
  inlineColor,
  textColor = '#00a7b5',
  borderColor = '#00a7b5'
) {
  const normalized = `${inlineColor || ''}`.trim().toLowerCase()

  if (
    !normalized ||
    normalized === 'inherit' ||
    normalized === '#000000' ||
    normalized === '#000' ||
    normalized === '#ffffff' ||
    normalized === '#fff' ||
    normalized === '#3b4c56'
  ) {
    return `${textColor || borderColor || '#00a7b5'}`.trim()
  }

  return `${inlineColor}`.trim()
}

function upsertInlineStyleColor(styles = '', textColor = '#00a7b5') {
  const cleaned = `${styles}`.replace(/\bcolor\s*:[^;"]*;?/gi, '')

  return `${cleaned}color:${textColor};`
}

function upsertTagStyleColor(tag, textColor) {
  if (/style=(["'])/i.test(tag)) {
    return tag.replace(/style=(["'])(.*?)\1/i, (_match, quote, styles) => {
      return `style=${quote}${upsertInlineStyleColor(styles, textColor)}${quote}`
    })
  }

  return tag.replace(/>$/, ` style="color:${textColor};">`)
}

export function bulletproofEditorialOutlinePillButtons(
  sourceHtml,
  { borderColor = '#00a7b5', textColor = '#00a7b5' } = {}
) {
  if (!sourceHtml) {
    return sourceHtml
  }

  const wrapped = sourceHtml.replace(
    /<a([^>]*class="[^"]*editorial-pill-cta-link[^"]*"[^>]*)>([\s\S]*?)<\/a>/gi,
    (match, attrs, label, offset, fullString) => {
      const before = fullString.slice(Math.max(0, offset - 160), offset)
      if (
        before.includes('editorial-pill-cta-cell') ||
        before.includes('editorial-pill-cta-bulletproof')
      ) {
        return match
      }

      const hrefMatch = attrs.match(/href="([^"]*)"/i)
      const href = hrefMatch ? hrefMatch[1] : '#'

      const dataMatch = attrs.match(/data-editorial-story-button="([^"]+)"/i)
      const dataAttr = dataMatch ? ` data-editorial-story-button="${dataMatch[1]}"` : ''

      const borderMatch = attrs.match(/border:\s*1px\s+solid\s+([^;"']+)/i)
      const colorMatch = attrs.match(/(?:^|\s)color:\s*([^;"']+)/i)
      const resolvedBorderColor = (borderMatch?.[1] || borderColor).trim()
      const resolvedTextColor = resolveEditorialPillTextColor(
        colorMatch?.[1],
        textColor,
        resolvedBorderColor
      )

      const linkStyle =
        `color:${resolvedTextColor};text-decoration:none;font-family:Inter,Arial,sans-serif;` +
        'font-size:13px;font-weight:600;letter-spacing:0.08em;line-height:1.2;white-space:nowrap;display:inline-block;' +
        'border:none;background-color:transparent;padding:0;border-radius:0;'
      const cellStyle =
        `border:1px solid ${resolvedBorderColor};border-radius:999px;background-color:#ffffff;` +
        `padding:12px 36px;mso-border-alt:1px solid ${resolvedBorderColor};text-align:center;`

      return (
        '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" ' +
        'class="editorial-pill-cta-bulletproof" style="margin:0 auto;border-collapse:separate;border-spacing:0;">' +
        `<tr><td class="editorial-pill-cta-cell" align="center"${dataAttr} style="${cellStyle}">` +
        `<a href="${href}" class="editorial-pill-cta-link editorial-pill-cta-link-inner" style="${linkStyle}">${label}</a>` +
        '</td></tr></table>'
      )
    }
  )

  return sanitizeBulletproofEditorialPillLinks(wrapped, textColor)
}

function sanitizeBulletproofEditorialPillLinks(sourceHtml, textColor = '#00a7b5') {
  return sourceHtml.replace(
    /(<td[^>]*class="[^"]*editorial-pill-cta-cell[^"]*"[^>]*>[\s\S]*?<a[^>]*class="[^"]*editorial-pill-cta-link[^"]*"[^>]*style=")([^"]*)(")/gi,
    (match, prefix, styles, suffix) => {
      const cleaned = styles
        .replace(/\bborder(?:-radius)?\s*:[^;"]*;?/gi, '')
        .replace(/\bbackground-color\s*:[^;"]*;?/gi, '')
        .replace(/\bpadding\s*:[^;"]*;?/gi, '')

      return (
        `${prefix}${cleaned}` +
        `color:${textColor};border:none;background-color:transparent;padding:0;border-radius:0;` +
        suffix
      )
    }
  )
}

const FOOTER_TEMPLATE_VARIABLE_KEYS = [
  'hotelPhone',
  'hotelName',
  'hotelAddress',
  'hotelCityStateZip',
  'hotelWebsite',
  'hotelWebsiteUrl',
  'footerAccentColor'
]

export function substituteFooterTemplateVariables(sourceHtml, variables = {}) {
  if (!sourceHtml) {
    return sourceHtml
  }

  let html = sourceHtml

  FOOTER_TEMPLATE_VARIABLE_KEYS.forEach(key => {
    const value = variables[key]
    if (value == null || `${value}`.trim().length === 0) {
      return
    }

    html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), `${value}`)
  })

  return html
}

export function applyEditorialPillLinkTextColors(sourceHtml, textColor = '#00a7b5') {
  if (!sourceHtml || !textColor) {
    return sourceHtml
  }

  const pillAnchorPattern =
    /<a\b(?=[^>]*class=["'][^"']*editorial-pill-cta-link[^"']*["'])[^>]*>/gi

  let html = sourceHtml.replace(pillAnchorPattern, tag => upsertTagStyleColor(tag, textColor))

  html = html.replace(
    /(<td[^>]*class="[^"]*editorial-pill-cta[^"]*"[^>]*>\s*<div[^>]*style=")([^"]*)(")/gi,
    (_match, prefix, styles, suffix) =>
      `${prefix}${upsertInlineStyleColor(styles, textColor)}${suffix}`
  )

  return html
}

export function normalizeRuntimePlaceholderLinksInMarkdown(markdown = '') {
  if (!markdown) {
    return markdown
  }

  return RUNTIME_MARKDOWN_LINK_REPLACEMENTS.reduce(
    (result, { pattern, replacement }) => result.replace(pattern, replacement),
    markdown
  )
}

export function normalizeRuntimePlaceholderLinksInHtml(html = '') {
  if (!html) {
    return html
  }

  let normalizedHtml = ENCODED_RUNTIME_PLACEHOLDER_HREFS.reduce(
    (result, { pattern, replacement }) => result.replace(pattern, replacement),
    html
  )

  normalizedHtml = BROKEN_UNSUBSCRIBE_LITERALS.reduce(
    (result, { pattern, replacement }) => result.replace(pattern, replacement),
    normalizedHtml
  )

  return normalizedHtml
}

export function suppressEmptyEditorialStoryButtons(sourceHtml, variables = {}) {
  if (!sourceHtml) {
    return sourceHtml
  }

  let html = sourceHtml

  ;['col1', 'col2', 'col3'].forEach(columnKey => {
    if (!hasEditorialStoryButtonLabel(variables, columnKey)) {
      html = removeEditorialStoryButtonWrap(html, columnKey)
    }
  })

  return html
}

export function injectInviteEmailCtaText(sourceHtml, ctaText) {
  if (!sourceHtml || ctaText == null || ctaText === '') {
    return sourceHtml
  }

  const encodedCtaText = escapeHtml(ctaText)

  if (sourceHtml.includes('{{ctaText}}')) {
    return sourceHtml.replace(/\{\{ctaText\}\}/g, encodedCtaText)
  }

  let html = sourceHtml.replace(
    /(<a\b(?=[^>]*class="[^"]*\bresort-solid-cta-link\b)[^>]*>)([\s\S]*?)(<\/a>)/gi,
    `$1${encodedCtaText}$3`
  )

  html = html.replace(
    /(<a\b(?=[^>]*class="[^"]*\beditorial-pill-cta-link\b)(?![^>]*data-editorial-story-button)[^>]*>)([\s\S]*?)(<\/a>)/gi,
    `$1${encodedCtaText}$3`
  )

  html = html.replace(
    /<(table|td|div)\b([^>]*class="[^"]*\bmjml-cta\b[^"]*"[^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag, attrs, content) => {
      if (/\bmjml-cta-(?:story|secondary|row)\b/i.test(`${attrs} ${match}`)) {
        return match
      }

      if (!/<a\b/i.test(content)) {
        return match
      }

      const updatedContent = content.replace(
        /(<a\b[^>]*>)([\s\S]*?)(<\/a>)/i,
        `$1${encodedCtaText}$3`
      )

      return `<${tag}${attrs}>${updatedContent}</${tag}>`
    }
  )

  return html
}

export function renderCampaignCustomizationHtml(shellHtml, { emailTitle, bodyHtml, ctaText }) {
  if (!shellHtml) {
    return shellHtml
  }

  let html = injectInviteEmailBodyHtml(shellHtml, bodyHtml || '')
  html = injectInviteEmailTitle(html, emailTitle)
  html = injectInviteEmailCtaText(html, ctaText)

  return makeCtaLinksFullyClickable(html)
}
